import { useApolloClient } from '@apollo/client';
import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';
import { ModalDialog } from '../../../modal/layout/ModalDialog';
import { ModalHeader } from '../../../modal/layout/ModalHeader';
import classNames from '../../../../utils/classNames';
import warning from '../../../../utils/logger';
import type {
    EditorRenderRuntime,
    ElementDefinition,
    ElementInsertionOption,
    ElementInsertionPresetOption,
    ElementRenderResult,
    RenderContext,
    RenderContextKind,
} from '../elements/types';
import { toErrorMessage } from '../forms/errorMessage';
import { SwarmTypeBadges } from '../SwarmTypeBadges';

interface AddElementSlotPresetModalProps {
    runtime: EditorRenderRuntime;
    contextKind: RenderContextKind;
    renderContext: RenderContext;
    selectedPresetParentOption: ElementInsertionOption | null;
    onClose: () => void;
    onSelectPreset: (preset: ElementInsertionPresetOption) => void;
}

const MODAL_PREVIEW_STAGE = {
    width: 340,
    height: 120,
    scale: 0.72,
    visibleHeight: 96,
} as const;

function PresetPreviewError({ error }: FallbackProps): JSX.Element {
    return (
        <div
            role="alert"
            className="mb-3 flex h-24 w-full flex-col items-center justify-center overflow-hidden rounded border border-red-200 bg-red-50 px-3 text-center text-xs text-red-700"
        >
            <span className="font-semibold">{gettext('PRESET_PREVIEW_RENDER_ERROR')}</span>
            <span className="mt-1">{toErrorMessage(error)}</span>
        </div>
    );
}

function PresetRenderPreviewContent({
    runtime,
    contextKind,
    renderContext,
    createDefinition,
}: {
    runtime: EditorRenderRuntime;
    contextKind: RenderContextKind;
    renderContext: RenderContext;
    createDefinition: () => ElementDefinition;
}): JSX.Element {
    const previewRuntime = useMemo(() => runtime.getPreviewRuntime(), [runtime]);
    const [readyFailure, setReadyFailure] = useState<{ reason: unknown } | null>(null);

    const result = useMemo((): ElementRenderResult => {
        if (renderContext.kind !== contextKind) {
            throw new Error(
                `Preset preview context mismatch: expected "${contextKind}", received "${renderContext.kind}".`
            );
        }

        return previewRuntime.withElementLocation(
            {
                elementPath: [0],
                listPath: [],
                contextKind,
                slotKind: 'child-list',
                index: 0,
                siblingCount: 1,
            },
            () =>
                previewRuntime
                    .createElementFromDefinition(createDefinition())
                    .toReactNode(renderContext, previewRuntime)
        );
    }, [contextKind, createDefinition, previewRuntime, renderContext]);

    useEffect(() => {
        let isActive = true;

        void result.ready.catch((error: unknown) => {
            if (isActive) {
                setReadyFailure({ reason: error });
            }
        });

        return () => {
            isActive = false;
        };
    }, [result]);

    if (readyFailure) {
        throw readyFailure.reason;
    }

    const stageWidth = MODAL_PREVIEW_STAGE.width / MODAL_PREVIEW_STAGE.scale;
    const stageHeight = MODAL_PREVIEW_STAGE.height / MODAL_PREVIEW_STAGE.scale;
    const visibleContentHeight = MODAL_PREVIEW_STAGE.visibleHeight / MODAL_PREVIEW_STAGE.scale;

    return (
        <div
            data-rb-preset-preview="true"
            className="mb-3 flex w-full items-center justify-center overflow-hidden rounded"
            style={{ height: MODAL_PREVIEW_STAGE.visibleHeight }}
        >
            <div className="flex h-full w-full items-center justify-center overflow-hidden text-xs text-slate-500">
                <div
                    className="flex items-center justify-center"
                    style={{
                        width: `${stageWidth}px`,
                        height: `${stageHeight}px`,
                        zoom: MODAL_PREVIEW_STAGE.scale,
                    }}
                >
                    <div
                        className="flex w-full flex-col overflow-hidden rounded bg-slate-50"
                        style={
                            {
                                height: visibleContentHeight,
                                // Preset cards render outside a page frame, but remain inside
                                // its DOM tree and inherit page or row height variables.
                                '--rb-flowable-container-height': `${visibleContentHeight}px`,
                            } as CSSProperties
                        }
                    >
                        <div className="my-auto w-full shrink-0">{result.node}</div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function PresetRenderPreview({
    runtime,
    contextKind,
    renderContext,
    createDefinition,
    elementType,
    presetKey,
}: {
    runtime: EditorRenderRuntime;
    contextKind: RenderContextKind;
    renderContext: RenderContext;
    createDefinition: () => ElementDefinition;
    elementType: string;
    presetKey: string;
}): JSX.Element {
    return (
        <ErrorBoundary
            resetKeys={[
                runtime,
                contextKind,
                renderContext,
                createDefinition,
                elementType,
                presetKey,
            ]}
            onError={(error) =>
                warning(
                    `Preset preview could not be rendered for: type=${elementType} preset=${presetKey}`,
                    error
                )
            }
            FallbackComponent={PresetPreviewError}
        >
            <PresetRenderPreviewContent
                runtime={runtime}
                contextKind={contextKind}
                renderContext={renderContext}
                createDefinition={createDefinition}
            />
        </ErrorBoundary>
    );
}

export function AddElementSlotPresetModal({
    runtime,
    contextKind,
    renderContext,
    selectedPresetParentOption,
    onClose,
    onSelectPreset,
}: AddElementSlotPresetModalProps): JSX.Element | null {
    const client = useApolloClient();
    const [presets, setPresets] = useState<readonly ElementInsertionPresetOption[]>([]);
    const [isLoadingPresets, setIsLoadingPresets] = useState(true);
    const [presetError, setPresetError] = useState<Error | null>(null);

    useEffect(() => {
        if (!selectedPresetParentOption) {
            setPresets([]);
            setPresetError(null);
            setIsLoadingPresets(true);
            return () => undefined;
        }

        let isActive = true;
        setPresets([]);
        setPresetError(null);
        setIsLoadingPresets(true);

        void selectedPresetParentOption
            .resolvePresets({ context: renderContext, client })
            .then((resolvedPresets) => {
                if (!isActive) {
                    return;
                }

                setPresets(resolvedPresets);
                setIsLoadingPresets(false);
            })
            .catch((error) => {
                if (!isActive) {
                    return;
                }

                setPresetError(error instanceof Error ? error : new Error(String(error)));
                setIsLoadingPresets(false);
            });

        return () => {
            isActive = false;
        };
    }, [client, renderContext, selectedPresetParentOption]);

    if (!selectedPresetParentOption) {
        return null;
    }

    return (
        <ModalDialog
            open={true}
            onClose={onClose}
            closeOnClickOutside={true}
            closeOnEsc={true}
            className="max-w-3xl"
        >
            <div className="relative p-4">
                <ModalHeader
                    onClose={onClose}
                    title={interpolate(
                        gettext('PRESET_MODAL_SELECT_PRESET_TITLE'),
                        {
                            label: selectedPresetParentOption.label,
                        },
                        true
                    )}
                />
                {isLoadingPresets ? (
                    <p className="mb-3 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                        {gettext('LOADING')}
                    </p>
                ) : null}
                {presetError ? (
                    <p className="mb-3 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                        {gettext('PRESET_MODAL_LOAD_ERROR')}
                    </p>
                ) : null}
                {!isLoadingPresets && !presetError && presets.length === 0 ? (
                    <p className="mb-3 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                        {gettext('PRESET_MODAL_NO_COMPATIBLE_PRESETS')}
                    </p>
                ) : null}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {presets.map((preset) => {
                        const isEnabled = selectedPresetParentOption.isEnabled && preset.isEnabled;

                        return (
                            <button
                                key={`${selectedPresetParentOption.type}:${preset.key}`}
                                type="button"
                                aria-label={preset.label}
                                disabled={!isEnabled}
                                onClick={() => onSelectPreset(preset)}
                                className={classNames(
                                    'rounded border p-3 text-left transition',
                                    isEnabled
                                        ? 'border-slate-200 hover:border-slate-400 hover:bg-slate-50'
                                        : 'cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400'
                                )}
                            >
                                <PresetRenderPreview
                                    runtime={runtime}
                                    contextKind={contextKind}
                                    renderContext={renderContext}
                                    createDefinition={preset.createDefinition}
                                    elementType={selectedPresetParentOption.type}
                                    presetKey={preset.key}
                                />
                                {preset.sourceLabel ? (
                                    <span className="mb-2 inline-flex rounded-full bg-teal-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-teal-700">
                                        {preset.sourceLabel}
                                    </span>
                                ) : null}
                                <div className="mb-2">
                                    <SwarmTypeBadges
                                        supportedSwarmTypes={preset.supportedSwarmTypes}
                                    />
                                </div>
                                <p className="text-sm font-semibold text-slate-800">
                                    {preset.label}
                                </p>
                                {preset.description ? (
                                    <p className="mt-1 text-xs text-slate-500">
                                        {preset.description}
                                    </p>
                                ) : null}
                            </button>
                        );
                    })}
                </div>
            </div>
        </ModalDialog>
    );
}
